import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { products } from '../data/mock';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    country: '',
    email: '',
    phone: '',
    product: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProductChange = (value) => {
    setFormData((prev) => ({ ...prev, product: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const scriptUrl = process.env.REACT_APP_GOOGLE_SCRIPT_URL;

      if (!scriptUrl) {
        throw new Error('Script URL not configured');
      }

      // URLSearchParams avoids CORS preflight with Google Apps Script
      const body = new URLSearchParams({
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: formData.phone,
        country: formData.country,
        product: formData.product || 'General Inquiry',
        message: formData.message,
        submitted_at: new Date().toLocaleString('en-IN'),
      });

      // no-cors is required for Google Apps Script cross-origin requests
      await fetch(scriptUrl, {
        method: 'POST',
        mode: 'no-cors',
        body,
      });

      // With no-cors we can't read the response — assume success
      toast.success('Quote request submitted! We will contact you within 24–48 hours.');
      setFormData({
        name: '',
        company: '',
        country: '',
        email: '',
        phone: '',
        product: '',
        message: ''
      });

    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit. Please email us directly at admin@rockgranites.in');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-[#FAFAF8] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold text-[#111111] mb-4">Get in Touch</h1>
          <p className="text-lg text-gray-600 max-w-3xl">
            Ready to start your project? Request a quote and receive a response within 24-48 hours. Free samples available for qualified buyers.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-[#111111] mb-6">Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#FAFAF8] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="text-[#111111]" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#111111] mb-1">Email</h3>
                      <a href="mailto:admin@rockgranites.in" className="text-gray-600 hover:text-[#111111] transition-colors">
                        admin@rockgranites.in
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#FAFAF8] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="text-[#111111]" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#111111] mb-1">Phone</h3>
                      <p className="text-gray-600">+91 9829070404</p>
                      <p className="!text-sm mt-1 text-gray-500">Monday - Saturday, 9 AM - 6 PM IST</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#FAFAF8] rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-[#111111]" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#111111] mb-1">Location</h3>
                      <p className="text-gray-600">India</p>
                      <p className="text-sm text-gray-500 mt-1">Serving global markets</p>
                    </div>
                  </div>
                </div>
              </div>

              <Card className="border-0 bg-[#111111] text-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-3">Response Time</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    We typically respond to all inquiries within 24-48 hours. For urgent requests, please mention it in your message.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Quote Request Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-[#111111] mb-6">Request a Quote</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="mt-2"
                          placeholder="John Doe" />

                      </div>
                      <div>
                        <Label htmlFor="company">Company Name *</Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          required
                          className="mt-2"
                          placeholder="Company Ltd." />

                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="mt-2"
                          placeholder="john@company.com" />

                      </div>
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="mt-2"
                          placeholder="+1 234 567 8900" />

                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="country">Country *</Label>
                        <Input
                          id="country"
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          required
                          className="mt-2"
                          placeholder="United States" />

                      </div>
                      <div>
                        <Label htmlFor="product">Product of Interest</Label>
                        <Select value={formData.product} onValueChange={handleProductChange}>
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Select a product" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General Inquiry</SelectItem>
                            {products.map((product) =>
                            <SelectItem key={product.id} value={product.id}>
                                {product.name}
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message">Project Details / Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="mt-2 resize-none"
                        placeholder="Please provide details about your project: quantity needed, timeline, finish preferences, and any specific requirements..." />

                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-[#111111] text-white hover:bg-[#2a2a2a]"
                      disabled={isSubmitting}>

                      {isSubmitting ?
                      'Submitting...' :

                      <>
                          Submit Request <Send className="ml-2" size={18} />
                        </>
                      }
                    </Button>

                    <p className="text-sm text-gray-500 text-center">
                      By submitting this form, you agree to be contacted regarding your inquiry.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-0">
        <div className="w-full h-96 overflow-hidden">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3561.5!2d74.8082672!3d26.8139469!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x396b8d4c9ff781a1%3A0xfc32f80bf5bedecd!2sRock%20Granites%20(A%20Unit%20Of%20Chandak%20Granite)!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Rock Granites - Kishangarh, Ajmer, Rajasthan"
          ></iframe>
        </div>
      </section>
    </div>);

};

export default Contact;